import { useNavigate, useLocation } from "react-router-dom";
import { CircleUserRound, ArrowLeft, ShoppingCart, UserPlus } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useTenant } from "../../context/TenantContext";
import { useCart } from "../../context/CartContext";

/**
 * Reusable header for the public shop pages.
 * - Left: tenant logo/name (+ optional back button)
 * - Right: "My Portal" with user first name when logged in, otherwise "Login"
 *   The Login button carries ?next=<current path> so users are returned to where
 *   they were shopping after signing in — not the member dashboard.
 */
export default function ShopHeader({ tenantSlug, backTo = null, backLabel = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { tenant } = useTenant();
  const cart = useCart();
  const firstName = (user?.name || "").trim().split(" ")[0] || (user?.email || "").split("@")[0] || "";
  const goLogin = () => {
    const next = encodeURIComponent(location.pathname + location.search);
    navigate(`/${tenantSlug}/login?next=${next}`);
  };
  return (
    <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-slate-200">
      <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-2 sm:gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {backTo && (
            <button
              onClick={() => navigate(backTo)}
              className="p-2 hover:bg-slate-100 rounded-full flex-shrink-0"
              data-testid="shop-header-back"
              aria-label={backLabel || "Back"}
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
          )}
          <button
            onClick={() => navigate(`/${tenantSlug}`)}
            className="flex items-center gap-3 min-w-0 hover:opacity-80"
            data-testid="shop-header-logo"
          >
            {tenant?.logo_url && <img src={tenant.logo_url} alt={tenant?.name} className="h-8 w-auto" />}
            <div className="text-left min-w-0">
              <p className="font-bold text-[#1e3a5f] text-sm leading-tight truncate">{tenant?.name || "My Maitree"}</p>
              <p className="text-[10px] text-slate-400" data-testid="shop-header-subtitle">{tenant?.tagline || "Event Shop"}</p>
            </div>
          </button>
        </div>
        {user ? (
          <div className="flex items-center gap-2">
            <button
              onClick={() => cart?.open?.()}
              className="relative p-2 rounded-full bg-slate-100 hover:bg-slate-200 text-[#1e3a5f]"
              data-testid="shop-header-cart"
              title="Cart"
            >
              <ShoppingCart className="w-5 h-5" />
              {cart && cart.count > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center" data-testid="shop-header-cart-count">{cart.count}</span>
              )}
            </button>
            <button
              onClick={() => navigate(`/${tenantSlug}/dashboard`)}
              className="flex items-center gap-2 pl-1 pr-3 sm:pr-4 py-1 rounded-full bg-[#1e3a5f] text-white text-sm font-medium max-w-[200px]"
              data-testid="shop-header-portal"
              title="Go to My Portal"
            >
              <span className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold flex-shrink-0">
                {(firstName[0] || "?").toUpperCase()}
              </span>
              <span className="truncate hidden sm:inline">Hi, {firstName || "there"}</span>
              <span className="truncate sm:hidden">Hi</span>
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <button
              onClick={() => cart?.open?.()}
              className="relative p-2 rounded-full bg-slate-100 hover:bg-slate-200 text-[#1e3a5f]"
              data-testid="shop-header-cart"
              title="Cart"
            >
              <ShoppingCart className="w-5 h-5" />
              {cart && cart.count > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center" data-testid="shop-header-cart-count">{cart.count}</span>
              )}
            </button>
            <button
              onClick={() => navigate(`/${tenantSlug}/member-register`)}
              className="hidden sm:flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium"
              data-testid="shop-header-become-member"
              title="Membership Application"
            >
              <UserPlus className="w-4 h-4" /> <span>Become a Member</span>
            </button>
            <button
              onClick={() => navigate(`/${tenantSlug}/member-register`)}
              className="sm:hidden flex items-center gap-1 px-2.5 py-2 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold"
              data-testid="shop-header-become-member-mobile"
              title="Become a Member"
            >
              <UserPlus className="w-4 h-4" /> Join
            </button>
            <button
              onClick={goLogin}
              className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-full bg-[#1e3a5f] text-white text-xs sm:text-sm font-semibold sm:font-medium"
              data-testid="shop-header-login"
            >
              <CircleUserRound className="w-4 h-4" /> Login
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
